import { useMemo } from "react";
import { trpc } from "@/lib/trpc";

/**
 * Hook that fetches the FPC taxonomy from the server and returns a
 * `fpcLabels` map (code → Slovak name) plus an `isLoading` flag.
 */
export function useFpcLabels() {
  const { data: fpcData, isLoading } = trpc.fpc.getTaxonomy.useQuery();

  const fpcLabels = useMemo(() => {
    if (!fpcData) return {} as Record<string, string>;
    const labels: Record<string, string> = {};
    fpcData.forEach(item => {
      labels[item.kod] = item.nazovSk;
    });
    return labels;
  }, [fpcData]);

  return { fpcLabels, isLoading };
}

/**
 * Get the display label for a given FPC code.
 * Falls back to the code itself if not found in the map.
 */
export function getFpcLabel(
  fpcLabels: Record<string, string>,
  code: string
): string {
  return fpcLabels[code] ?? code;
}

/**
 * Get the level of an FPC code (1–4) based on its length.
 * Level 1 = 1 char, Level 2 = 3 chars, Level 3 = 4 chars, Level 4 = 5 chars.
 */
export function getFpcLevel(code: string): 1 | 2 | 3 | 4 {
  if (code.length >= 5) return 4;
  if (code.length === 4) return 3;
  if (code.length === 3) return 2;
  return 1;
}

/**
 * Get the prefix for a given level from an FPC code.
 * level 4 = full 5-char code, level 3 = 4-char, level 2 = 3-char, level 1 = 1-char.
 */
export function getFpcPrefix(code: string, level: 1 | 2 | 3 | 4): string {
  const len = level === 4 ? 5 : level === 3 ? 4 : level === 2 ? 3 : 1;
  return code.substring(0, len);
}

/**
 * Get the section header label for a given level and FPC code.
 * e.g. level 3 of "M01AA" → "Rovnaká skupina: Hydina (M01A)"
 */
export function getFpcSectionLabel(
  fpcLabels: Record<string, string>,
  code: string,
  level: 1 | 2 | 3 | 4
): string {
  const prefix = getFpcPrefix(code, level);
  const label = getFpcLabel(fpcLabels, prefix);
  const levelName =
    level === 4
      ? "Rovnaký typ"
      : level === 3
        ? "Rovnaká skupina"
        : level === 2
          ? "Rovnaká podkategória"
          : "Rovnaká hlavná kategória";
  return `${levelName}: ${label} (${prefix})`;
}
